import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";

const heroImg = "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=2400&q=85";

const experiences = [
  { tag: "Sip", title: "The Social Bar",       to: "/the-social-bar",     img: "/media/social-bar.jpg",
    copy: "Premium mobile bartending featuring signature cocktails, custom drink menus, professional bartending, and personalized event details." },
  { tag: "Vibe", title: "The Social Set",      to: "/the-social-set",     img: "https://customer-assets-gfyr7b9c.emergentagent.net/job_simply-social-events/artifacts/couqhl0g_image000001.webp",
    copy: "DJ services designed to bring the energy, read the room, and keep the party moving." },
  { tag: "Party", title: "The Social Nightclub", to: "/nightclub-rentals", img: "/media/nightclub.jpg",
    copy: "Transform an ordinary space into your own private nightlife experience with our inflatable nightclub rentals." },
  { tag: "Customize", title: "The Social Supply", to: "/the-social-supply", img: "/media/social-supply.jpg",
    copy: "Custom event details designed to make celebrations feel personal, intentional, and completely yours." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden grain on-dark" data-testid="home-hero">
        <img src={heroImg} alt="" aria-hidden="true"
             className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0.55) 0%, rgba(10,10,11,0.35) 40%, rgba(10,10,11,0.95) 100%)" }} />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 pt-40 md:pt-48 pb-16 min-h-[100svh] flex flex-col justify-end">
          <div className="font-label text-xs md:text-sm mb-6" style={{ color: "var(--ss-gold)" }}>
            Chicago • St. Louis • Surrounding Areas
          </div>
          <h1 className="font-display font-light tracking-tighter leading-[0.9] text-[13vw] md:text-[9vw] xl:text-[8rem] max-w-[16ch]">
            Your Event.<br/>
            <span style={{ color: "var(--ss-rust)" }}>Our</span> Social Experience.
          </h1>
          <p className="mt-8 font-body text-base md:text-xl max-w-2xl" style={{ color: "var(--ss-cream-dim)" }}>
            From the first pour to the last song, Simply Social brings together bartending, music, nightlife experiences, and custom details to create events people actually remember.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="#experiences" onClick={(e) => { e.preventDefault(); document.getElementById("experiences")?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid="hero-explore-btn" className="btn-outline">
              Explore Our Experiences <ArrowUpRight size={14} />
            </Link>
            <Link to="/book" data-testid="hero-book-btn" className="btn-primary">
              Book Your Event <ArrowUpRight className="arrow" size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-40" data-testid="home-intro">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>01 — Simply Social</div>
          </div>
          <div className="md:col-span-7">
            <Reveal>
              <h2 className="font-display font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl">
                We Don't Just Show Up.<br/>
                <span style={{ color: "var(--ss-cream-dim)" }}>We Bring the Experience.</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 font-body text-lg md:text-xl leading-relaxed" style={{ color: "var(--ss-cream-dim)" }}>
                Book one service or build the full experience. Whether it's a private cocktail hour, a rooftop party, or a wedding weekend, our four Simply Social experiences work independently — and together — to make your event feel considered, energetic, and completely yours.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section id="experiences" className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32" data-testid="home-experiences">
        <div className="flex items-end justify-between mb-12 md:mb-20">
          <div>
            <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>02 — Our Experiences</div>
            <h2 className="mt-4 font-display font-light tracking-tight text-4xl md:text-6xl">Four ways in.</h2>
          </div>
        </div>
        <div className="grid gap-10 md:gap-16 md:grid-cols-2">
          {experiences.map((e, i) => (
            <Reveal key={e.to} delay={i * 80}>
              <Link to={e.to} data-testid={`experience-card-${e.to.replace(/\//g,'')}`} className="group block">
                <div className="relative overflow-hidden aspect-[4/5] on-dark" style={{ background: "var(--ss-bg-2)" }}>
                  {e.video ? (
                    <video src={e.video} poster={e.img} autoPlay muted loop playsInline aria-hidden="true"
                           className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
                  ) : (
                    <img src={e.img} alt={e.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
                  )}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0) 40%, rgba(10,10,11,0.85) 100%)" }} />
                  <div className="absolute left-6 top-6 font-label text-xs" style={{ color: "var(--ss-gold)" }}>{e.tag}.</div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="font-display text-3xl md:text-5xl leading-none tracking-tight">{e.title}</div>
                  </div>
                </div>
                <p className="mt-5 font-body text-base md:text-lg max-w-lg" style={{ color: "var(--ss-cream-dim)" }}>{e.copy}</p>
                <div className="mt-4 inline-flex items-center gap-2 font-label text-xs text-[color:var(--ss-rust)] group-hover:gap-3 transition-all">
                  Explore <ArrowUpRight size={14} />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BUNDLE */}
      <section className="relative overflow-hidden mt-12" data-testid="home-bundle" style={{ background: "var(--ss-bg-2)" }}>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-label text-xs mb-6" style={{ color: "var(--ss-gold)" }}>03 — The Bundle</div>
            <Reveal>
              <h2 className="font-display font-light tracking-tight text-5xl md:text-7xl leading-[0.95]">
                Why Choose <span style={{ color: "var(--ss-rust)" }}>One?</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 font-body text-lg md:text-xl max-w-xl leading-relaxed" style={{ color: "var(--ss-cream-dim)" }}>
                Build your own Simply Social experience by combining bartending, DJ services, nightlife rentals, and custom details. Exclusive bundled pricing may be available when multiple Simply Social services are booked together.
              </p>
            </Reveal>
            <Link to="/build" data-testid="bundle-cta-btn" className="btn-gold mt-10">
              Build Your Experience <ArrowUpRight className="arrow" size={14} />
            </Link>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-4">
            {experiences.map(e => (
              <div key={e.to} className="relative aspect-square overflow-hidden on-dark">
                {e.video ? (
                  <video src={e.video} poster={e.img} autoPlay muted loop playsInline aria-hidden="true"
                         className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <img src={e.img} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
                )}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,14,12,0.2), rgba(15,14,12,0.75))" }} />
                <div className="absolute bottom-3 left-3 font-label text-[0.65rem]" style={{ color: "var(--ss-cream)" }}>{e.tag}.</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="border-y overflow-hidden on-dark" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid="home-marquee">
        <div className="ticker font-display text-4xl md:text-6xl" style={{ color: "var(--ss-cream-dim)" }}>
          {Array.from({ length: 2 }).map((_, r) => (
            <div key={r} className="flex gap-16 pr-16">
              {["Weddings", "Corporate", "Rooftops", "Private", "Pop-ups", "Birthdays", "Launches", "Weekends"].map(w => (
                <span key={r + w} className="flex items-center gap-16">
                  <span>{w}</span>
                  <span style={{ color: "var(--ss-rust)" }}>·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* AREAS */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 md:py-32 flex flex-col md:flex-row items-start gap-10" data-testid="home-areas">
        <div className="flex-1">
          <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>04 — Service Area</div>
          <h2 className="mt-4 font-display font-light tracking-tight text-4xl md:text-6xl">Chicago. St. Louis. Everywhere in between.</h2>
        </div>
        <div className="flex-1 flex flex-col gap-3 font-body text-lg" style={{ color: "var(--ss-cream-dim)" }}>
          <div className="flex items-center gap-3"><MapPin size={18} style={{ color: "var(--ss-rust)" }} /> Chicago, IL</div>
          <div className="flex items-center gap-3"><MapPin size={18} style={{ color: "var(--ss-rust)" }} /> St. Louis, MO</div>
          <div className="flex items-center gap-3"><MapPin size={18} style={{ color: "var(--ss-rust)" }} /> Surrounding Areas</div>
          <Link to="/book" data-testid="home-areas-book-btn" className="btn-primary mt-6 w-fit">Book Your Event <ArrowUpRight className="arrow" size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
